<template>
    <div class="cr-page">
        <!-- Header -->
        <div class="cr-header">
            <div class="cr-header-left">
                <div>
                    <h1 class="cr-title">Cost Approvals</h1>
                    <p class="cr-subtitle">Drag cards between columns to update status</p>
                </div>
            </div>
            <div class="cr-header-right">
                <div class="cr-search-wrap">
                    <i class="fas fa-search cr-search-icon"></i>
                    <input class="cr-search" v-model="search" placeholder="Search requests..." />
                </div>
                <select class="cr-filter-select" v-model="filterCategory">
                    <option value="">All Categories</option>
                    <option v-for="c of masters.categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
                <select class="cr-filter-select" v-model="filterPriority">
                    <option value="">All Priorities</option>
                    <option v-for="p of masters.priorities" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
                <button class="cr-btn cr-btn-clear" v-if="filterCategory || filterPriority || search"
                    @click="clearFilters">
                    <i class="fas fa-times"></i> Clear
                </button>
                <button class="cr-btn cr-btn-primary" @click="$router.push('/cost-requests/create')">
                    <i class="fas fa-plus"></i> New Request
                </button>
            </div>
        </div>

        <!-- Stats Strip -->
        <div class="cr-stats">
            <div class="cr-stat" v-for="col of columns" :key="col.code">
                <span class="cr-stat-dot" :style="{ background: col.color }"></span>
                <span class="cr-stat-label">{{ col.name }}</span>
                <span class="cr-stat-count">{{ getColumnItems(col.code).length }}</span>
            </div>
            <div class="cr-stat">
                <span class="cr-stat-dot" style="background:#64748b"></span>
                <span class="cr-stat-label">Total</span>
                <span class="cr-stat-count">{{ allRequests.length }}</span>
            </div>
        </div>

        <!-- Loading -->
        <div class="cr-loading" v-if="loading">
            <i class="fas fa-circle-notch fa-spin"></i> Loading requests...
        </div>

        <!-- Kanban Board -->
        <div class="cr-board" v-else>
            <div class="cr-column" v-for="col of columns" :key="col.code" @dragover.prevent
                @dragenter.prevent="dragEnterCol(col.code)" @drop="dropCard(col.code)"
                :class="{ 'cr-col-dragover': dragOverCol === col.code }">
                <div class="cr-col-header">
                    <span class="cr-col-dot" :style="{ background: col.color }"></span>
                    <span class="cr-col-title">{{ col.name }}</span>
                    <span class="cr-col-count">{{ getColumnItems(col.code).length }}</span>
                </div>
                <div class="cr-col-body">
                    <div v-if="!getColumnItems(col.code).length" class="cr-col-empty">
                        <i class="fas fa-inbox"></i>
                        <span>No requests</span>
                    </div>
                    <CostRequestCard v-for="item of getColumnItems(col.code)" :key="item.id" :request="item"
                        :draggable="true" @dragstart.native="dragStart(item)" @dragend.native="dragEnd"
                        @click.native="$router.push(`/cost-requests/${item.id}`)" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import CostRequestCard from "../../components/cost-request/CostRequestCard.vue"

const API_BASE = "/cost-approval-requests"

// Mapping from status code to the lifecycle endpoint to transition INTO that status
const TRANSITION_MAP = {
    pending_approval: { endpoint: "submit", from: ["draft"] },
    approved: { endpoint: "approve", from: ["pending_approval"] },
    rejected: { endpoint: "reject", from: ["pending_approval"] },
    info_requested: { endpoint: "request-info", from: ["pending_approval"] },
    cancelled: { endpoint: "cancel", from: ["draft", "pending_approval", "info_requested"] },
    settled: { endpoint: "settle", from: ["verified"] },
}

export default {
    components: { CostRequestCard },
    data() {
        return {
            columns: [],
            allRequests: [],
            masters: { categories: [], statuses: [], priorities: [], tiers: [], settings: {} },
            loading: false,
            search: "",
            filterCategory: "",
            filterPriority: "",
            // drag state
            dragItem: null,
            dragOverCol: null,
        }
    },
    async mounted() {
        await this.fetchMasters()
        await this.fetchBoard()
    },
    methods: {
        async fetchMasters() {
            try {
                const res = await http.get(`${API_BASE}/masters`)
                const data = res.data.result || res.data || {}
                this.masters = {
                    categories: data.categories || [],
                    statuses: data.statuses || [],
                    priorities: data.priorities || [],
                    tiers: data.tiers || [],
                    settings: data.settings || {},
                }
            } catch (err) {
                console.error("Error fetching masters", err)
            }
        },
        async fetchBoard() {
            this.loading = true
            try {
                const res = await http.get(`${API_BASE}/board`)
                const data = res.data.result || res.data || {}
                const boardColumns = data.columns || []
                // Build columns from board response (each column has status object)
                this.columns = boardColumns.map((col) => ({
                    code: col.status.code,
                    name: col.status.name,
                    color: col.status.color,
                    sortOrder: col.status.sortOrder,
                }))
                // Flatten all requests from board columns
                this.allRequests = []
                boardColumns.forEach((col) => {
                    if (col.requests && Array.isArray(col.requests)) {
                        this.allRequests.push(...col.requests)
                    }
                })
            } catch (err) {
                console.error("Error fetching board", err)
                this.allRequests = []
            }
            this.loading = false
        },
        getStatusCode(request) {
            if (request.status && typeof request.status === "object") return request.status.code
            return request.status || ""
        },
        getColumnItems(statusCode) {
            return this.filteredRequests.filter((r) => this.getStatusCode(r) === statusCode)
        },
        // Drag & Drop
        dragStart(item) {
            this.dragItem = item
        },
        dragEnd() {
            this.dragItem = null
            this.dragOverCol = null
        },
        dragEnterCol(colKey) {
            this.dragOverCol = colKey
        },
        async dropCard(newStatusCode) {
            if (!this.dragItem) { this.dragOverCol = null; return }
            const currentCode = this.getStatusCode(this.dragItem)
            if (currentCode === newStatusCode) { this.dragOverCol = null; return }

            const transition = TRANSITION_MAP[newStatusCode]
            if (!transition || !transition.from.includes(currentCode)) {
                this.dragOverCol = null
                this.$toast?.error?.(`Cannot move from ${currentCode} to ${newStatusCode}`)
                return
            }

            // Save old status for revert
            const oldStatus = this.dragItem.status
            // Optimistically update
            if (typeof this.dragItem.status === "object") {
                const col = this.columns.find((c) => c.code === newStatusCode)
                this.dragItem.status = col ? { ...col } : { code: newStatusCode }
            } else {
                this.dragItem.status = newStatusCode
            }
            this.dragOverCol = null

            try {
                await http.patch(`${API_BASE}/${this.dragItem.id}/${transition.endpoint}`, {})
                const colName = this.columns.find((c) => c.code === newStatusCode)?.name || newStatusCode
                this.$toast?.success?.(`Moved to ${colName}`)
            } catch (err) {
                console.error("Error updating status", err)
                this.dragItem.status = oldStatus
                const msg = err.response?.data?.message || "Failed to update status"
                this.$toast?.error?.(msg)
            }
        },
        clearFilters() {
            this.filterCategory = ""
            this.filterPriority = ""
            this.search = ""
        },
    },
    computed: {
        filteredRequests() {
            let items = this.allRequests
            // Category filter
            if (this.filterCategory) {
                items = items.filter((r) => {
                    const catId = r.category?.id || r.categoryId
                    return catId == this.filterCategory
                })
            }
            // Priority filter
            if (this.filterPriority) {
                items = items.filter((r) => {
                    const prioId = r.priority?.id || r.priorityId
                    return prioId == this.filterPriority
                })
            }
            // Text search
            if (this.search) {
                const s = this.search.toLowerCase()
                items = items.filter(
                    (r) =>
                        (r.title && r.title.toLowerCase().includes(s)) ||
                        (r.requestNumber && r.requestNumber.toLowerCase().includes(s)) ||
                        (r.requestedBy?.fullName && r.requestedBy.fullName.toLowerCase().includes(s)) ||
                        (r.department?.name && r.department.name.toLowerCase().includes(s)) ||
                        (r.description && r.description.toLowerCase().includes(s))
                )
            }
            return items
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cr-page {
    padding: 20px;
}

/* Header */
.cr-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    gap: 12px;
    flex-wrap: wrap;
}

.cr-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.cr-title {
    font-size: 20px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}

.cr-subtitle {
    font-size: 12.5px;
    color: $gray400;
    margin: 2px 0 0;
}

.cr-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
}

.cr-search-wrap {
    position: relative;
}

.cr-search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: $gray400;
}

.cr-search {
    border: 1px solid $gray200;
    border-radius: 8px;
    padding: 8px 12px 8px 30px;
    font-size: 13px;
    width: 200px;
    outline: none;
    font-family: inherit;
    transition: border $transitionFast;

    &:focus {
        border-color: $primaryColor;
    }
}

.cr-filter-select {
    border: 1px solid $gray200;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
    outline: none;
    font-family: inherit;
    background: white;
    color: $gray700;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    padding-right: 26px;
    cursor: pointer;
    transition: border $transitionFast;

    &:focus {
        border-color: $primaryColor;
    }
}

.cr-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    font-family: inherit;
    transition: all $transitionFast;
}

.cr-btn-primary {
    background: $primaryColor;
    color: white;

    &:hover {
        background: $primaryDark;
    }
}

.cr-btn-clear {
    background: $gray100;
    color: $gray600;
    padding: 8px 12px;

    &:hover {
        background: $gray200;
    }
}

/* Stats */
.cr-stats {
    display: flex;
    gap: 6px;
    margin-bottom: 16px;
    flex-wrap: wrap;
}

.cr-stat {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusFull;
    font-size: 12px;
}

.cr-stat-dot {
    width: 8px;
    height: 8px;
    border-radius: $radiusFull;
    flex-shrink: 0;
}

.cr-stat-label {
    color: $gray500;
    font-weight: 500;
}

.cr-stat-count {
    font-weight: 700;
    color: $gray800;
}

/* Loading */
.cr-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 60px 20px;
    font-size: 14px;
    color: $gray400;

    i {
        font-size: 18px;
        color: $primaryColor;
    }
}

/* Board */
.cr-board {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 16px;
    -webkit-overflow-scrolling: touch;
    min-height: calc(100vh - 220px);
}

/* Column */
.cr-column {
    min-width: 260px;
    width: 260px;
    flex-shrink: 0;
    background: $gray50;
    border: 1px solid $gray100;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    transition: border-color $transitionFast, background $transitionFast;
}

.cr-col-dragover {
    border-color: $primaryColor;
    background: rgba(0, 94, 137, .03);
    border-style: dashed;
}

.cr-col-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px 14px;
    border-bottom: 1px solid $gray100;
}

.cr-col-dot {
    width: 10px;
    height: 10px;
    border-radius: $radiusFull;
    flex-shrink: 0;
}

.cr-col-title {
    font-size: 12.5px;
    font-weight: 700;
    color: $gray700;
    flex: 1;
}

.cr-col-count {
    font-size: 11px;
    font-weight: 700;
    background: $gray200;
    color: $gray600;
    padding: 1px 7px;
    border-radius: $radiusFull;
}

.cr-col-body {
    flex: 1;
    padding: 10px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cr-col-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 30px 10px;
    color: $gray300;
    font-size: 12px;

    i {
        font-size: 20px;
    }
}
</style>
